<template>
  <div
    data-testid="transaction-matching-results-v2__view"
    class="transaction-matching-results-v2"
  >
    <!-- Finished / In Progress States -->
    <div v-if="isResultReady">
      <!-- Tabs from annexure list -->
      <v-tabs
        v-if="annexureTabs.length > 0"
        v-model="activeTab"
        data-testid="transaction-matching-results-v2__tabs"
        color="primary"
        class="results-v2-tabs"
        :show-arrows="false"
      >
        <v-tab
          v-for="(tab, index) in annexureTabs"
          :key="tab.collection"
          data-testid="transaction-matching-results-v2__tab"
          :id="'automation-test-v2-' + index"
          class="results-v2-tabs__tab"
        >
          <span class="results-v2-tabs__label">
            {{ tab.displayName }}
          </span>
        </v-tab>
      </v-tabs>

      <!-- Tab Content -->
      <div
        v-for="(tab, index) in annexureTabs"
        :key="'content-' + tab.collection"
        data-testid="transaction-matching-results-v2__tab-content"
        class="mt-3"
      >
        <!-- Only mount the active tab's component (lazy loading) -->
        <template v-if="activeTab === index">
          <!-- Summary Tab: special component -->
          <TMResultsSummaryV2
            v-if="tab.name === 'Summary'"
            :collection="tab.collection"
            :analysis-details="analysisDetails"
            :run-details="runDetails"
            :file-config-info="fileConfigInfo"
            :file-list="fileList"
            :tab-name="tab.name"
            :download-history="downloadHistory"
            :loading-history="loadingHistory"
            @generate-result="onGenerateResult"
            @refresh-download-history="$emit('refresh-download-history')"
            @download-history-file="$emit('download-history-file', $event)"
          />

          <!-- Report Tab: dedicated report component -->
          <TMResultsReportV2
            v-else-if="tab.name === 'Report'"
            :collection="tab.collection"
            :analysis-details="analysisDetails"
            :run-details="runDetails"
            :file-config-info="fileConfigInfo"
            :tab-name="tab.name"
            :download-history="downloadHistory"
            :loading-history="loadingHistory"
            :result-types="resultTypes"
            @generate-result="onGenerateResult"
            @refresh-download-history="$emit('refresh-download-history')"
            @download-history-file="$emit('download-history-file', $event)"
          />

          <!-- All other tabs: generic data table -->
          <TMResultsDataTable
            v-else
            :ref="'dataTable_' + index"
            variant="v2"
            :collection="tab.collection"
            :tab-name="tab.name"
            :file-config-id="tab.fileConfigId"
            :analysis-details="analysisDetails"
            :run-details="runDetails"
            :file-config-info="fileConfigInfo"
            :download-history="downloadHistory"
            :loading-history="loadingHistory"
            @generate-result="onGenerateResult"
            @refresh-download-history="$emit('refresh-download-history')"
            @download-history-file="$emit('download-history-file', $event)"
            @refresh-data="$emit('refresh-data')"
          />
        </template>
      </div>
      <!-- Empty State: No annexures -->
      <div
        v-if="annexureTabs.length === 0"
        data-testid="transaction-matching-results-v2__no-annexures"
        class="text-center grey--text title pa-6"
      >
        No result data available
      </div>
    </div>

    <!-- Not Started -->
    <div
      v-else-if="statusCheck === TM_STATUS.NOT_STARTED"
      data-testid="transaction-matching-results-v2__not-started"
      class="text-center grey--text title pa-6"
    >
      No data available (Transaction Matching)
    </div>

    <!-- Failed -->
    <div
      v-else-if="statusCheck === TM_STATUS.FAILED"
      data-testid="transaction-matching-results-v2__failed"
      class="text-center grey--text title pa-6"
    >
      No data available (Transaction Matching Execution Failed)
    </div>

    <!-- In Progress / Queued -->
    <div
      v-else
      data-testid="transaction-matching-results-v2__in-progress"
      class="text-center grey--text title pa-6"
    >
      No data available (Transaction Matching Execution In Progress)
    </div>
  </div>
</template>

<script>
/**
 * TransactionMatchingResultsV2 Component
 *
 * Self-contained V2 results page for Transaction Matching.
 * Dynamically builds tabs from runDetails.annexure and renders:
 * - Summary tab: TMResultsSummaryV2 (special rendering)
 * - All other tabs: TMResultsDataTable (generic data table with filter/sort/pagination)
 *
 * No AI / Matched / Unmatched tabs — each annexure entry becomes its own tab.
 *
 * @component
 */

import TMResultsDataTable from '../shared/TMResultsDataTable.vue';
import TMResultsSummaryV2 from './TMResultsSummaryV2.vue';
import TMResultsReportV2 from './TMResultsReportV2.vue';
import { TM_STATUS } from '../../../../utils/constants.js';

export default {
  name: 'TransactionMatchingResultsV2',

  components: {
    TMResultsDataTable,
    TMResultsSummaryV2,
    TMResultsReportV2
  },

  props: {
    /**
     * Analysis details object containing bpid, _id, filecount, etc.
     */
    analysisDetails: {
      type: Object,
      required: true,
      default: () => ({})
    },

    /**
     * Single V2 rundetail object (not keyed by verification).
     * Contains annexure[], parameterdetail, stage, etc.
     */
    runDetails: {
      type: Object,
      required: true,
      default: () => ({})
    },

    /**
     * File configuration info containing column types and mappings
     */
    fileConfigInfo: {
      type: Object,
      default: () => ({})
    },

    /**
     * Array of file config objects from setup.file, each with _id and name.
     * Used to resolve fileConfig IDs to human-readable names in headers.
     */
    fileList: {
      type: Array,
      default: () => []
    },

    /**
     * Overall loading state from parent
     */
    loading: {
      type: Boolean,
      default: false
    },

    /**
     * Array of download history files for DownloadExcel dropdown
     */
    downloadHistory: {
      type: Array,
      default: () => []
    },

    /**
     * Loading state for download history
     */
    loadingHistory: {
      type: Boolean,
      default: false
    },

    /**
     * Current execution status (TM_STATUS value)
     */
    statusCheck: {
      type: [Number, String],
      default: TM_STATUS.NOT_STARTED
    },

    /**
     * Result types from formData. Passed down to TMResultsReportV2 to gate
     * features like the Pending Payouts drill-down button.
     */
    resultTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      TM_STATUS,
      activeTab: 0
    };
  },

  computed: {
    /**
     * Set of tab names that require explicit enablement via analysisDetails.results.
     * Only "report" and "commission/commissions" are restricted.
     * All other tabs are shown unconditionally.
     * @returns {Set<string>}
     */
    restrictedTabs() {
      return new Set(['report', 'commission']);
    },

    excludedTabs() {
      return new Set(['pending payout']);
    },

    /**
     * Normalised set of result names from analysisDetails.results.
     * Used to enable restricted tabs when present.
     * @returns {Set<string>}
     */
    enabledResultNames() {
      const results = this.analysisDetails?.results;
      if (!Array.isArray(results)) return new Set();
      return new Set(results.map(r => (r || '').toLowerCase().replace(/s$/, '')));
    },

    /**
     * Map of fileConfig ID → human-readable name from fileList.
     * @returns {Object<string, string>}
     */
    fileNameMap() {
      const map = {};
      if (this.fileList && this.fileList.length > 0) {
        for (const file of this.fileList) {
          if (file._id) {
            map[file._id] = file.name || file.alias || file._id;
          }
        }
      }
      return map;
    },

    /**
     * Build tab list from runDetails.annexure array.
     * - Report and Commission/Commissions tabs only shown when in analysisDetails.results
     * - All other tabs are always shown
     * - Case-insensitive matching with trailing 's' stripped
     * - Data table tabs ("Table 1", "Table 2", etc.) are renamed to the
     *   corresponding file name from fileList, matched by order.
     * @returns {Array<{name: string, displayName: string, collection: string}>}
     */
    annexureTabs() {
      const annexure = this.runDetails?.annexure;
      if (!annexure || !Array.isArray(annexure)) return [];

      const enabled = this.enabledResultNames;
      const specialTabs = new Set(['summary', 'report', 'commission', 'commissions']);

      // Ordered file names and IDs for positional matching with data table tabs
      const files = this.fileList || [];
      const fileNames = files.map(f => f.name || f.alias || f._id || '');

      // Build reverse lookup: file._id -> fileConfig key
      const fileIdToConfigId = {};
      for (const [configId, config] of Object.entries(this.fileConfigInfo || {})) {
        if (config && config.file) {
          fileIdToConfigId[config.file] = configId;
        }
      }

      let dataTabIndex = 0;
      
      return annexure
        .filter(item => {
          if (!item || !item.collection) return false;
          
          const normalized = (item.name || '').toLowerCase().replace(/s$/, '');

          // Always hide excluded tabs
          if (this.excludedTabs.has(normalized)) {
            return false;
          }
          
          // If this is a restricted tab, only show it when enabled
          if (this.restrictedTabs.has(normalized)) {
            return enabled.has(normalized);
          }
          
          // All other tabs are always shown
          return true;
        })
        .map(item => {
          const name = item.name || '';
          const nameLower = name.toLowerCase();
          let displayName = item.displayName || name;

          // For data table tabs (not Summary/Report/Commission),
          // use the file name from fileList in order and resolve fileConfigId
          let fileConfigId = '';
          if (!specialTabs.has(nameLower)) {
            if (dataTabIndex < files.length) {
              if (fileNames[dataTabIndex]) {
                displayName = fileNames[dataTabIndex];
              }
              const fileId = files[dataTabIndex]._id || '';
              fileConfigId = fileIdToConfigId[fileId] || '';
            }
            dataTabIndex++;
          }

          return { name, displayName, collection: item.collection, fileConfigId };
        });
    },

    /**
     * Determine if results are ready to display.
     * True when execution is finished, AI in progress, or general in progress.
     * @returns {boolean}
     */
    isResultReady() {
      return [
        TM_STATUS.FINISHED,
        TM_STATUS.AI_IN_PROGRESS,
        TM_STATUS.IN_PROGRESS
      ].includes(this.statusCheck);
    }
  },

  watch: {
    /**
     * Reset active tab when runDetails changes (e.g., re-execution)
     */
    runDetails: {
      handler() {
        this.activeTab = 0;
      },
      deep: false
    }
  },

  methods: {
    /**
     * Handle generate-result from TMResultsDataTable.
     * Merges the section name with the tables payload and emits upward.
     * @param {Object} payload - { tables: ["Table 1"] }
     */
    onGenerateResult(payload) {
      this.$emit('generate-result', {
        section: 'TMResults',
        tables: payload?.tables || [],
        collection: payload?.collection || null,
        dataFilters: payload?.dataFilters || null
      });
    }
  },

  beforeDestroy() {
    this.activeTab = 0;
  }
};
</script>

<style scoped lang="scss">
.transaction-matching-results-v2 {
  width: 100%;
  padding: var(--space-2) 0;
}

.results-v2-tabs {
  // Hide disabled arrow buttons; show only when actually needed
  ::v-deep .v-slide-group__prev--disabled,
  ::v-deep .v-slide-group__next--disabled {
    display: none !important;
  }

  // Tabs sit left-aligned, not stretched
  ::v-deep .v-slide-group__content {
    display: flex;
    width: auto;
    gap: 2px; // gap-0.5 equivalent
  }

  // The tabs bar itself acts as the bottom border
  ::v-deep .v-tabs-bar {
    border-bottom: 1px solid var(--border-on, #e5e7eb);
    height: 42px;
  }

  // Remove Vuetify's default active indicator line
  ::v-deep .v-tabs-slider-wrapper {
    display: none;
  }
}

.results-v2-tabs__tab {
  flex: 0 0 auto !important;
  min-width: 0 !important;
  max-width: none !important;
  text-transform: none;
  letter-spacing: 0.025em;
  font-size: var(--font-size-xs, 0.875rem);
  position: relative;
  overflow: visible;
  white-space: nowrap;
  padding: 0 1.25rem; // px-5
  margin-bottom: -1px; // -mb-px: overlap the bar's bottom border

  // Inactive state
  border: 1px solid var(--border-on, #e5e7eb);
  border-bottom: none;
  background: var(--color-surface-muted, #f3f4f6);
  color: var(--color-text-secondary, #6b7280);
  border-top-left-radius: var(--radius-4, 0.5rem);
  border-top-right-radius: var(--radius-4, 0.5rem);
  transition: all var(--duration-fast, 0.15s) var(--ease-standard, ease);

  &:hover {
    background: var(--color-surface-hover, #f9fafb);
    color: var(--color-text-primary, #374151);
  }

  // Active state
  &.v-tab--active {
    background: var(--color-surface-default, #fff);
    color: var(--color-text-heading, #1a2e44);
    font-weight: 600;
    border-color: var(--border-on, #d1d5db);
    box-shadow: var(--shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05));
    z-index: 1;
  }
}

.results-v2-tabs__label {
  display: block;
  white-space: nowrap;
}
</style>
